// Overhead Allocation — Allocation Register (MA-R3, Feb 2026)
//
// Read-only view of the append-only Allocation Lines ledger with
// filters (period / source / target / rule / run) and a summary
// panel that nets reversals per (source, target) for the chosen
// period. This is the report Finance uses to answer "how much
// overhead landed on each Revenue Project this month?".
import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { PageHeader, Card } from "@/components/ui-helpers";
import { RefreshCw, Undo2, ClipboardList } from "lucide-react";
import { toast, Toaster } from "sonner";

const fmtMoney = (n) => `₹${(Number(n) || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
const fmtNum   = (n) => (Number(n) || 0).toLocaleString(undefined, { maximumFractionDigits: 4 });

export default function AllocationRegisterPage() {
  const [rulesFacet, setRulesFacet] = useState([]);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [summary, setSummary] = useState({ rows: [], total_net: 0 });
  const [filter, setFilter] = useState({
    period_key: "",
    rule_id: "",
    source_project_id: "",
    target_project_id: "",
    include_reversals: true,
  });
  const [facets, setFacets] = useState(null);
  const [loading, setLoading] = useState(false);

  async function loadFacets() {
    try {
      const [rules, f] = await Promise.all([
        api.get("/mca/allocation-rules?limit=500"),
        api.get("/mca/allocation-rules/facets"),
      ]);
      setRulesFacet(rules.data.rows || []);
      setFacets(f.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load filters");
    }
  }

  async function load() {
    setLoading(true);
    try {
      const q = new URLSearchParams();
      if (filter.period_key)        q.set("period_key",        filter.period_key);
      if (filter.rule_id)           q.set("rule_id",           filter.rule_id);
      if (filter.source_project_id) q.set("source_project_id", filter.source_project_id);
      if (filter.target_project_id) q.set("target_project_id", filter.target_project_id);
      q.set("include_reversals", filter.include_reversals ? "true" : "false");
      const { data } = await api.get(`/mca/allocation-lines?${q.toString()}`);
      setRows(data.rows || []);
      setTotal(data.total || 0);
      // Summary is per-period only
      if (filter.period_key) {
        const s = await api.get(`/mca/allocation-lines/summary?period_key=${filter.period_key}`);
        setSummary(s.data);
      } else {
        setSummary({ rows: [], total_net: 0 });
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load register");
    } finally { setLoading(false); }
  }

  useEffect(() => { loadFacets(); }, []);
  useEffect(() => { load(); }, [
    filter.period_key, filter.rule_id, filter.source_project_id,
    filter.target_project_id, filter.include_reversals,
  ]);

  return (
    <div data-testid="allocation-register-page">
      <Toaster richColors position="top-right" />
      <PageHeader
        eyebrow="Overhead Allocation · MA-R3"
        title="Allocation Register"
        subtitle="Append-only ledger of every Overhead Allocation line. Filter by period / source / target / rule. The summary panel nets reversals per (source, target) so you can see the true amount landed on each Revenue Project."
        actions={
          <button onClick={load}
            className="border border-slate-300 hover:bg-slate-50 text-slate-700 px-4 py-2.5 text-xs uppercase tracking-wider font-bold flex items-center gap-2"
            data-testid="allocation-register-refresh">
            <RefreshCw size={14} /> Refresh
          </button>
        }
      />

      {/* Filter row */}
      <Card className="mb-4 p-4 flex flex-wrap gap-3 items-end">
        <div className="min-w-[160px]">
          <label className={labelCls}>Period</label>
          <input type="month" value={filter.period_key}
            onChange={e => setFilter({ ...filter, period_key: e.target.value })}
            className={inputCls} data-testid="allocation-register-period-filter" />
        </div>
        <div className="min-w-[220px]">
          <label className={labelCls}>Rule</label>
          <select value={filter.rule_id}
            onChange={e => setFilter({ ...filter, rule_id: e.target.value })}
            className={inputCls} data-testid="allocation-register-rule-filter">
            <option value="">All rules</option>
            {rulesFacet.map(r => <option key={r.id} value={r.id}>{r.code} · {r.name}</option>)}
          </select>
        </div>
        <div className="min-w-[220px]">
          <label className={labelCls}>Source Project</label>
          <select value={filter.source_project_id}
            onChange={e => setFilter({ ...filter, source_project_id: e.target.value })}
            className={inputCls} data-testid="allocation-register-source-filter">
            <option value="">All sources</option>
            {(facets?.source_projects || []).map(p =>
              <option key={p.id} value={p.id}>{p.project_code} · {p.project_name}</option>)}
          </select>
        </div>
        <div className="min-w-[220px]">
          <label className={labelCls}>Target Project</label>
          <select value={filter.target_project_id}
            onChange={e => setFilter({ ...filter, target_project_id: e.target.value })}
            className={inputCls} data-testid="allocation-register-target-filter">
            <option value="">All targets</option>
            {(facets?.target_projects || []).map(p =>
              <option key={p.id} value={p.id}>{p.project_code} · {p.project_name}</option>)}
          </select>
        </div>
        <label className="flex items-center gap-2 text-[12px] pb-2">
          <input type="checkbox" checked={filter.include_reversals}
            onChange={e => setFilter({ ...filter, include_reversals: e.target.checked })}
            data-testid="allocation-register-include-reversals" />
          Show reversals
        </label>
        <button onClick={() => setFilter({
          period_key: "", rule_id: "", source_project_id: "",
          target_project_id: "", include_reversals: true,
        })}
          className="px-4 py-2.5 border border-slate-300 text-slate-700 hover:bg-slate-50 text-xs uppercase tracking-wider font-bold"
          data-testid="allocation-register-clear">Clear</button>
      </Card>

      {/* Summary panel — visible only when period filter is set */}
      {filter.period_key && summary.rows.length > 0 && (
        <Card className="mb-4">
          <div className="p-3 border-b border-slate-200 flex items-center justify-between">
            <div className="text-xs uppercase tracking-wider font-bold text-slate-500">
              Net summary · {filter.period_key} · Σ net = {fmtMoney(summary.total_net)}
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-[12px]" data-testid="allocation-register-summary">
              <thead className="bg-slate-50">
                <tr className="text-left">
                  <Th>Source</Th>
                  <Th>Target</Th>
                  <Th className="text-right">Gross +</Th>
                  <Th className="text-right">Gross −</Th>
                  <Th className="text-right">Net</Th>
                  <Th className="text-right">Lines</Th>
                </tr>
              </thead>
              <tbody>
                {summary.rows.map((r, i) => (
                  <tr key={i} className="border-t border-slate-100"
                      data-testid={`allocation-register-summary-row-${i}`}>
                    <td className="px-3 py-2">
                      <div className="font-mono text-[11px]">{r.source_project_code}</div>
                      <div className="text-[10px] text-slate-500">{r.source_project_name}</div>
                    </td>
                    <td className="px-3 py-2">
                      <div className="font-mono text-[11px]">{r.target_project_code}</div>
                      <div className="text-[10px] text-slate-500">{r.target_project_name}</div>
                    </td>
                    <td className="px-3 py-2 text-right font-mono text-emerald-700">{fmtMoney(r.gross_positive)}</td>
                    <td className="px-3 py-2 text-right font-mono text-red-600">{fmtMoney(r.gross_negative)}</td>
                    <td className="px-3 py-2 text-right font-mono font-bold">{fmtMoney(r.net_allocated)}</td>
                    <td className="px-3 py-2 text-right font-mono">{r.line_count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Line register */}
      <Card>
        <div className="p-3 border-b border-slate-200 text-xs uppercase tracking-wider font-bold text-slate-500">
          {loading ? "Loading…" : `${total} line${total === 1 ? "" : "s"}`}
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-[12px]" data-testid="allocation-register-table">
            <thead className="bg-slate-50">
              <tr className="text-left">
                <Th>Period</Th>
                <Th>Run #</Th>
                <Th>Rule</Th>
                <Th>Source</Th>
                <Th>Target</Th>
                <Th>Basis</Th>
                <Th className="text-right">Weight</Th>
                <Th className="text-right">Pool</Th>
                <Th className="text-right">Allocated</Th>
                <Th>Type</Th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && !loading && (
                <tr><td colSpan="10" className="p-6 text-center text-slate-400 text-sm">
                  No allocation lines match the current filters.
                </td></tr>
              )}
              {rows.map(ln => (
                <tr key={ln.id} className="border-t border-slate-100 hover:bg-slate-50"
                    data-testid={`allocation-register-row-${ln.id}`}>
                  <td className="px-3 py-2 font-mono">{ln.period_key}</td>
                  <td className="px-3 py-2 font-mono text-[11px]">{ln.run_number}</td>
                  <td className="px-3 py-2 font-mono text-[11px]">{ln.rule_code}</td>
                  <td className="px-3 py-2">
                    <div className="font-mono text-[11px]">{ln.source_project_code}</div>
                    <div className="text-[10px] text-slate-500">{ln.source_project_name}</div>
                  </td>
                  <td className="px-3 py-2">
                    <div className="font-mono text-[11px]">{ln.target_project_code}</div>
                    <div className="text-[10px] text-slate-500">{ln.target_project_name}</div>
                  </td>
                  <td className="px-3 py-2 text-[11px]">{ln.basis_type}</td>
                  <td className="px-3 py-2 text-right font-mono">{fmtNum(ln.weight_percent)}%</td>
                  <td className="px-3 py-2 text-right font-mono">{fmtMoney(ln.source_indirect_pool)}</td>
                  <td className={`px-3 py-2 text-right font-mono font-bold ${ln.allocated_amount < 0 ? "text-red-600" : "text-slate-800"}`}>
                    {fmtMoney(ln.allocated_amount)}
                  </td>
                  <td className="px-3 py-2">
                    {ln.is_reversal ? (
                      <span className="inline-flex items-center gap-1 text-[10px] text-amber-700 border border-amber-300 bg-amber-50 px-2 py-0.5 uppercase tracking-wider font-bold">
                        <Undo2 size={10} /> Reversal
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-[10px] text-slate-600 border border-slate-300 bg-slate-50 px-2 py-0.5 uppercase tracking-wider font-bold">
                        <ClipboardList size={10} /> Original
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

const Th = ({ children, className = "" }) => (
  <th className={`px-3 py-2.5 text-[10px] uppercase tracking-wider font-bold text-slate-500 ${className}`}>{children}</th>
);
const inputCls = "w-full px-3 py-2.5 border border-slate-300 text-sm bg-white focus:border-[#F97316] focus:outline-none";
const labelCls = "block text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1";
